import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import {
    shouldTableSortByRenewTime,
    isDraftsMeaningfulSaveTimeEnabled,
} from 'owa-mail-triage-common';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';

/**
 * Compare the date time of two rows
 * @param targetLastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param targetLastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert
 * @param rowKeyOfReferenceRow the conversation rowKey of the reference row
 * @param tableView the table view
 * @returns
 * 1) < 0 if reference row is earlier than target row
 * 2) 0 if have the same time
 * 3) > 0 if reference row is later than target row
 */
export default function compareRowDateTime(
    targetLastDeliveryOrRenewTimeStamp: string,
    targetLastDeliveryTimeStamp: string,
    rowKeyOfReferenceRow: string,
    tableView: TableView
): number {
    const isDraftsFolder = folderIdToName(tableView.tableQuery.folderId) === 'drafts';
    // For Drafts, key the reference row by the service-stamped last meaningful save time so it
    // matches the target row (positioned the same way in onAddOrUpdateRow); fall back to
    // LastModifiedTime when the property is absent (older drafts predating the feature, or never
    // meaningfully saved). A mismatched key here would make the comparator asymmetric and
    // misplace or drop rows during notification repositioning. Only when the
    // tri-draftsMeaningfulSaveTime flight is on; with the flight off, key by LastModifiedTime
    // (pre-feature behavior) so it stays symmetric with the flight-off sort/reposition path.
    const useMeaningfulSaveTime =
        isDraftsFolder && isDraftsMeaningfulSaveTimeEnabled(getMailboxInfo(tableView));
    const lastDeliveryTimeStampOfReferenceRow = isDraftsFolder
        ? useMeaningfulSaveTime
            ? MailRowDataPropertyGetter.getLastMeaningfulDraftSaveTimeStamp(
                  rowKeyOfReferenceRow,
                  tableView
              ) ??
              MailRowDataPropertyGetter.getLastModifiedTimeStamp(rowKeyOfReferenceRow, tableView)
            : MailRowDataPropertyGetter.getLastModifiedTimeStamp(rowKeyOfReferenceRow, tableView)
        : MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKeyOfReferenceRow, tableView);

    const diffDeliveryUTCTime =
        new Date(lastDeliveryTimeStampOfReferenceRow).getTime() -
        new Date(targetLastDeliveryTimeStamp).getTime();

    if (shouldTableSortByRenewTime(tableView.tableQuery)) {
        // If table supports pin, use renew time as primary sort, and delivery time as secondary sort
        const diffRenewTimeInUTC =
            new Date(
                MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
                    rowKeyOfReferenceRow,
                    tableView
                )
            ).getTime() - new Date(targetLastDeliveryOrRenewTimeStamp).getTime();
        return diffRenewTimeInUTC || diffDeliveryUTCTime;
    }

    return diffDeliveryUTCTime;
}
